import { FC, useState } from 'react'
import { ActionIcon, Button, MultiSelect, Popover, Select, Stack, Tooltip, Group } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { VehicleResponse, ShiftResponse, AwareDatetimeInterval } from '@/api/main/types.gen'
import { moveScheduleMutation } from '@/api/main/@tanstack/react-query.gen'
import { invalidateQueriesById } from '@/lib/queryClient'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { TbArrowsExchange } from 'react-icons/tb'
import { formatInterval, getTimezone } from '@/utils/datetime'
import { useTranslation } from 'react-i18next'

dayjs.extend(utc)
dayjs.extend(timezone)

const areIntervalsOverlapping = (
  interval1: { start: string; end: string },
  interval2: { start: string; end: string }
): boolean => {
  const timezone = getTimezone()
  const start1 = dayjs.utc(interval1.start).tz(timezone)
  const end1 = dayjs.utc(interval1.end).tz(timezone)
  const start2 = dayjs.utc(interval2.start).tz(timezone)
  const end2 = dayjs.utc(interval2.end).tz(timezone)
  return end1.isAfter(start2) && start1.isBefore(end2)
}

const formatShiftInterval = (interval: AwareDatetimeInterval): string => formatInterval(interval, getTimezone())

const MoveScheduleButton: FC<{
  vehicles: Array<VehicleResponse>
  currentVehicle: VehicleResponse
  interval: AwareDatetimeInterval
  vehicleShifts: Array<ShiftResponse>
}> = ({ vehicles, currentVehicle, interval, vehicleShifts }) => {
  const { t } = useTranslation(['scheduling', 'common', 'vehicles'])
  const queryClient = useQueryClient()
  const [opened, setOpened] = useState(false)

  const { status, mutate } = useMutation({
    ...moveScheduleMutation(),
    onSuccess: () => {
      invalidateQueriesById(queryClient, ['getRequirementSubintervals', 'getScheduleForInterval'])
      showNotification({ message: t('drivesMovedSuccessfully'), color: 'green' })
      setOpened(false) // Close popover on success
    },
    onError: () => showNotification({ message: t('failedToMoveSchedule'), color: 'red' }),
  })

  const matchingShifts = vehicleShifts.filter(shift =>
    areIntervalsOverlapping(
      { start: shift.interval.starting_from, end: shift.interval.ending_at },
      { start: interval.starting_from, end: interval.ending_at }
    )
  )

  const form = useForm({
    initialValues: { destinationVehicleId: '', shiftsToReschedule: [] as string[] },
  })

  return (
    <Popover opened={opened} onChange={setOpened} closeOnClickOutside={false}>
      <Popover.Target>
        <Tooltip label={t('moveDrives')}>
          <ActionIcon variant="subtle" size="sm" loading={status === 'pending'} onClick={() => setOpened(o => !o)}>
            <TbArrowsExchange size={20} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <form
          onSubmit={form.onSubmit(values => {
            mutate({
              body: {
                interval_start: interval.starting_from,
                interval_end: interval.ending_at,
                source_vehicle_id: currentVehicle.id,
                destination_vehicle_id: values.destinationVehicleId,
                shifts_to_reschedule: values.shiftsToReschedule.map(Number),
              },
            })
          })}
        >
          <Stack p="10px">
            <Select
              clearable
              searchable
              label={t('destinationVehicle')}
              placeholder={t('noVehicleSelected')}
              data={vehicles
                .filter(v => v.id !== currentVehicle.id)
                .map(v => ({
                  value: v.id,
                  label: v.id,
                }))}
              {...form.getInputProps('destinationVehicleId')}
            />
            <MultiSelect
              label={t('shiftsToReschedule')}
              data={matchingShifts.map(shift => ({
                value: shift.id.toString(),
                label: `${formatShiftInterval(shift.interval)}, ${shift.driver?.name ?? t('noDriver', { ns: 'vehicles' })}`,
              }))}
              {...form.getInputProps('shiftsToReschedule')}
            />
            <Group justify="space-between">
              <Button type="submit" loading={status === 'pending'}>
                {t('move')}
              </Button>
              <Button variant="subtle" onClick={() => setOpened(false)}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </Group>
          </Stack>
        </form>
      </Popover.Dropdown>
    </Popover>
  )
}

export default MoveScheduleButton
